import apiService from "@/lib/api";

const CANDIDATE_BASE = "/api/v2/candidate";
const FILE_BASE = "/api/v2/file";
const AI_TOOL = "/api/v2/ai-tools";
const RESUME_TEMPLATE_BASE = "/api/v2/resume-template";

export const CandidateService = {
  getConsentText: async () => {
    try {
      const response = await apiService.get(
        `${CANDIDATE_BASE}/consent-text`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching consent text:", error);
      throw error;
    }
  },

  finalizeCandidateConfirmation: async (token) => {
    try {
      const response = await apiService.post(
        `${CANDIDATE_BASE}/candidate-confirm/finalize?token=${token}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Mints a fresh confirmation link for a token in any state — live, expired,
  // or replaced by an earlier resend. No auth header: the token is the credential.
  resendCandidateConfirmation: async (token) => {
    try {
      const response = await apiService.post(
        `${CANDIDATE_BASE}/candidate-confirm/resend?token=${token}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Pre-extraction gate — call BEFORE extractResumeData/reuploadResumeExtract
  // so an invalid/mismatched file never reaches the expensive extraction call.
  validateResume: async (selectedFile, jobId) => {
    try {
      const formData = new FormData();
      formData.append("resume", selectedFile);
      formData.append("job_id", jobId);

      const response = await apiService.post(
        `${CANDIDATE_BASE}/resume-validate`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (error) {
      console.error("Error while validating the resume:", error);
      throw error;
    }
  },

  extractResumeData: async (selectedFile, jobId, config = {}) => {
    try {
      const formData = new FormData();
      formData.append("resume", selectedFile);
      formData.append("job_id", jobId);

      const response = await apiService.post(`${CANDIDATE_BASE}/apply`, formData, {
        ...config,
        headers: {
          ...(config.headers || {}),
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error while Extracting the candidate data:", error);
      throw error;
    }
  },

  reviewCandidateDetail: async (candidateId, application_id) => {
    try {
      const response = await apiService.get(
        `${CANDIDATE_BASE}/candidate-review/${candidateId}`,
        { params: { application_id } }
      );
      return response.data;
    } catch (error) {
      console.error("Error while review candidate data:", error);
      throw error;
    }
  },

  confirmCandidateDetail: async (
    candidateId,
    candidateData,
    application_id,
    ai_consent = false
  ) => {
    try {
      const {
        current_salary,
        expected_salary,
        notice_period,
        total_experience,
        instagram_url,
        facebook_url,
        twitter_url,
        // Screening answers travel at the top level of the confirm body (the
        // server reads them there, not inside draft_data). Pull them out so
        // they don't get folded into `rest`.
        question_answers,
        alternate_email,
        // Top-level on the request — the server writes it to
        // applications.languages. Leaving it inside `rest` would bury it in
        // draft_data and the column would stay null.
        languages,
        ...rest
      } = candidateData;

      const payload = {
        draft_data: rest,
        current_salary,
        expected_salary,
        notice_period,
        total_experience,
        instagram_url,
        facebook_url,
        twitter_url,
        alternate_email,
        languages: Array.isArray(languages) ? languages : undefined,
        ai_consent,
        ...(Array.isArray(question_answers) ? { question_answers } : {}),
      };

      const response = await apiService.put(
        `${CANDIDATE_BASE}/candidate-confirm/${candidateId}`,
        payload,
        { params: { application_id } }
      );

      return response.data;
    } catch (error) {
      console.error("Error while review candidate data:", error);
      throw error;
    }
  },

  fetchAnalysis: async (params) => {
    try {
      const response = await apiService.getSecure(
        `${CANDIDATE_BASE}/detail/${params?.candidateId}?job_id=${params?.jobId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching candidate analysis:", error);
      throw error;
    }
  },

  // flowId is required by the endpoint, as a query param — it must match
  // the application's job's own status_flow_id, checked before the
  // transition is attempted.
  updateStatus: async (candidateId, statusId, applicationId, flowId, confirmed = true) => {
    try {
      // confirmed defaults true: every call site already gates this behind
      // its own confirm dialog before calling here — see
      // candidate-analysis/index.jsx's confirmStatusUpdate.
      const payload = { status_id: statusId, confirmed };

      const response = await apiService.patch(
        `${CANDIDATE_BASE}/applications/status/${candidateId}`,
        payload,
        { params: { application_id: applicationId, flow_id: flowId } }
      );

      return response.data;
    } catch (error) {
      console.error("Error while updating candidate status:", error);
      throw error;
    }
  },

  getInterviewStatus: async (applicationId, questionType) => {
    try {
      const params = questionType ? { question_type: questionType } : {};
      const response = await apiService.getSecure(
        `${AI_TOOL}/interview-questions/status/${applicationId}`,
        { params }
      );
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { status: "not_started", question_type: questionType };
      }
      throw error;
    }
  },

  generateInterviewQuestions: async (
    candidateId,
    applicationId,
    questionType,
    seniorityLevel,
    config = {}
  ) => {
    try {
      const typeParam = questionType ? `&question_type=${questionType}` : "";
      const levelParam = seniorityLevel ? `&seniority_level=${seniorityLevel}` : "";
      const response = await apiService.postSecure(
        `${AI_TOOL}/interview-questions/generate/${candidateId}?application_id=${applicationId}${typeParam}${levelParam}`,
        null,
        {
          responseType: "text",
          ...config,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error while generating interview questions:", error);
      throw error;
    }
  },

  submitCandidateFeedback: async (candidateId, applicationId, feedbackData) => {
    try {
      const response = await apiService.postSecure(
        `${CANDIDATE_BASE}/feedback/${candidateId}`,
        feedbackData,
        { params: { application_id: applicationId } }
      );
      return response.data;
    } catch (error) {
      console.error("Error while submitting candidate feedback:", error);
      throw error;
    }
  },

  fetCandidateList: async (params = {}) => {
    try {
      const response = await apiService.getSecure(`${CANDIDATE_BASE}/list`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Error while review candidate data:", error);
      throw error;
    }
  },

  /**
   * Dry run of updateCandidateResume — writes nothing, returns only the
   * `matchRetrigger` block the real PATCH would return.
   *
   * Lets the edit page gate its Save behind the "Re-run the AI match?" prompt:
   * score-affecting fields are detected server-side (single source of truth),
   * so the user's answer is known before anything is committed.
   */
  previewCandidateResumeRetrigger: async (candidateId, applicationId, candidateData) => {
    try {
      const response = await apiService.postSecure(
        `${CANDIDATE_BASE}/candidate-update-resume/${candidateId}/preview-retrigger`,
        candidateData,
        { params: { application_id: applicationId } }
      );
      return response.data;
    } catch (error) {
      console.error("Error while previewing candidate resume retrigger:", error);
      throw error;
    }
  },

  updateCandidateResume: async (candidateId, applicationId, candidateData) => {
    try {
      const response = await apiService.patch(
        `${CANDIDATE_BASE}/candidate-update-resume/${candidateId}`,
        candidateData,
        { params: { application_id: applicationId } }
      );
      return response.data;
    } catch (error) {
      console.error("Error while updating candidate resume data:", error);
      throw error;
    }
  },

  fetchCandidateList: async (jobId) => {
    try {
      const response = await apiService.getSecure(`${CANDIDATE_BASE}/list`, {
        params: { job_id: jobId },
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching candidate list for job ${jobId}:`, error);
      throw error;
    }
  },

  fetchTop5Candidates: async (jobId, excludeStatusId) => {
    try {
      const response = await apiService.getSecure(`${CANDIDATE_BASE}/list`, {
        params: {
          job_id: jobId,
          exclude_status_id: excludeStatusId,
          sort_by: "match_score",
          sort_order: "desc",
          per_page: 5,
          page: 1,
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching top 5 candidates for job ${jobId}:`, error);
      throw error;
    }
  },

  fetchJobInterviewQuestionTypes: async (jobId) => {
    try {
      const response = await apiService.getSecure(
        `${AI_TOOL}/interview-question-types/active`,
        {
          params: { job_id: jobId },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching interview question types:", error);
      throw error;
    }
  },

  fetchSeniorityLevels: async () => {
    try {
      const response = await apiService.getSecure(
        `${AI_TOOL}/enums/seniority-levels`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching seniority levels:", error);
      throw error;
    }
  },

  fetchCandidateResume: async (candidateId, applicationId) => {
    try {
      const response = await apiService.getSecure(
        `${CANDIDATE_BASE}/candidate-view-resume/${candidateId}`,
        {
          responseType: "blob",
          params: { application_id: applicationId },
        }
      );
      return response;
    } catch (error) {
      console.error(
        `Error fetching resume for candidate ${candidateId}:`,
        error
      );
      throw error;
    }
  },

  // Templates the current tenant has been granted access to (see
  // SuperAdmin's "Manage CV Templates" screen) — drives the picker on the
  // candidate page's "Export Formatted CV" button.
  listResumeTemplates: async () => {
    try {
      const response = await apiService.getSecure(`${RESUME_TEMPLATE_BASE}/list`);
      return response.data;
    } catch (error) {
      console.error("Error fetching resume templates:", error);
      throw error;
    }
  },

  // What `templateKey` actually looks like — no real candidate data, no AI call.
  //
  // Returns `{ blob, mimeType }` rather than just the Blob because the server
  // answers with a PDF where LibreOffice is installed (accurate, fixed-layout)
  // and falls back to the .docx where it isn't (approximate, rendered
  // in-browser). Callers pick their viewer from `mimeType` — don't assume
  // either format.
  //
  // `filled: true` asks for the template filled with a sample candidate instead
  // of the blank design.
  previewResumeTemplate: async (templateKey, { filled = false } = {}) => {
    try {
      const response = await apiService.getSecure(
        `${RESUME_TEMPLATE_BASE}/${templateKey}/preview`,
        { responseType: "blob", params: filled ? { filled: true } : undefined }
      );

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: response.headers?.["content-type"] });

      // An error envelope comes back as JSON even though we asked for a blob.
      if (blob.type?.includes("application/json")) {
        const text = await blob.text();
        let message = "Failed to load template preview";
        try {
          const errorData = JSON.parse(text);
          message = errorData?.detail || errorData?.message || message;
        } catch {
          // Non-JSON body — keep the generic message.
        }
        throw new Error(message);
      }

      // Trust the response header over blob.type — some proxies strip it.
      const mimeType = response.headers?.["content-type"] || blob.type || "";
      return { blob, mimeType };
    } catch (error) {
      console.error(`Error previewing resume template ${templateKey}:`, error);
      throw error;
    }
  },

  // Fills `templateKey`'s design with this candidate's already-saved resume
  // data (falling back to one AI call for anything the template needs that
  // isn't already known) and downloads the result — same blob/filename
  // handling as downloadCandidateResume below.
  generateFormattedResume: async (templateKey, candidateId, applicationId) => {
    try {
      const response = await apiService.postSecure(
        `${RESUME_TEMPLATE_BASE}/${templateKey}/generate/${candidateId}`,
        null,
        {
          responseType: "blob",
          params: applicationId ? { application_id: applicationId } : undefined,
        }
      );

      const contentDisposition = response?.headers?.["content-disposition"] || "";
      const filenameMatch = contentDisposition.match(
        /filename\*?=(?:UTF-8''|")?([^";]+)/i
      );
      const filename = filenameMatch
        ? decodeURIComponent(filenameMatch[1].replace(/"/g, ""))
        : "formatted-resume.docx";

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: response.headers?.["content-type"] });

      // An error envelope comes back as JSON even though we asked for a blob.
      // Parsed inside the try, thrown outside it: throwing from within meant
      // the catch below swallowed the server's own message ("This candidate has
      // no confirmed application data to export.") and replaced it with the
      // generic one every time.
      if (blob.type?.includes("application/json")) {
        const text = await blob.text();
        let message = "Failed to generate formatted CV";
        try {
          const errorData = JSON.parse(text);
          message = errorData?.detail || errorData?.message || message;
        } catch {
          // Non-JSON body — keep the generic message.
        }
        throw new Error(message);
      }

      const url = URL.createObjectURL(blob);
      try {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } finally {
        URL.revokeObjectURL(url);
      }

      return response;
    } catch (error) {
      console.error(
        `Error generating formatted resume for candidate ${candidateId}:`,
        error
      );
      throw error;
    }
  },

  downloadCandidateResume: async (candidateId, applicationId) => {
    try {
      const response = await apiService.getSecure(
        `${CANDIDATE_BASE}/candidate-download-resume/${candidateId}`,
        {
          responseType: "blob",
          params: { application_id: applicationId },
        }
      );

      const contentDisposition =
        response?.headers?.["content-disposition"] || "";
      const filenameMatch = contentDisposition.match(
        /filename\*?=(?:UTF-8''|")?([^";]+)/i
      );
      const filename = filenameMatch
        ? decodeURIComponent(filenameMatch[1].replace(/"/g, ""))
        : "resume.pdf";

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], {
              type: response.headers?.["content-type"],
            });

      if (blob.type?.includes("application/json")) {
        const text = await blob.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(
            errorData?.detail ||
              errorData?.message ||
              "Failed to download resume"
          );
        } catch {
          throw new Error("Failed to download resume");
        }
      }

      const url = URL.createObjectURL(blob);

      try {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } finally {
        URL.revokeObjectURL(url);
      }

      return response;
    } catch (error) {
      console.error(
        `Error downloading resume for candidate ${candidateId}:`,
        error
      );
      throw error;
    }
  },

  getCandidateStatusTransition: async (candidateId, applicationId) => {
    try {
      const response = await apiService.getSecure(
        `${CANDIDATE_BASE}/applications/status-transitions/${candidateId}?application_id=${applicationId}`
      );
      return response;
    } catch (error) {
      console.error(
        `Error fetching transitions for application ${applicationId}:`,
        error
      );
      throw error;
    }
  },

  /**
   * @param {boolean} attestation - The recruiter's ticked attestation checkbox.
   *   Required server-side: omitting it 422s, sending `false` fails with
   *   REUPLOAD_ATTESTATION_REQUIRED. Pass `true` only when the box is actually
   *   ticked — when a tenant turns the candidate confirmation email off, this
   *   is the entire consent trail for replacing a resume.
   */
  reuploadResumeExtract: async (candidateId, applicationId, file, attestation) => {
    try {
      const formData = new FormData();
      formData.append("resume", file);
      formData.append("application_id", applicationId);
      formData.append("attestation", String(attestation === true));

      const response = await apiService.postSecure(
        `${CANDIDATE_BASE}/resume-reupload/${candidateId}/extract`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (error) {
      console.error("Error extracting reuploaded resume:", error);
      throw error;
    }
  },

  reuploadResumeSubmit: async (candidateId, reuploadId, payload) => {
    try {
      const response = await apiService.patch(
        `${CANDIDATE_BASE}/resume-reupload/${candidateId}/${reuploadId}/submit`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error submitting reuploaded resume:", error);
      throw error;
    }
  },

  finalizeResumeReuploadConfirmation: async (token) => {
    try {
      const response = await apiService.post(
        `${CANDIDATE_BASE}/resume-reupload/finalize?token=${token}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Re-upload's resend replaces the row's single token — only the newest
  // re-upload email can be resent; an older copy answers CONFIRMATION_TOKEN_INVALID.
  resendResumeReuploadConfirmation: async (token) => {
    try {
      const response = await apiService.post(
        `${CANDIDATE_BASE}/resume-reupload/resend?token=${token}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getReuploadResumeConsentText: async () => {
    try {
      const response = await apiService.getSecure(
        `${CANDIDATE_BASE}/reupload-resume/consent-text`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching reupload consent text:", error);
      throw error;
    }
  },

  triggerJobFitAnalysis: async (candidateId) => {
    try {
      const response = await apiService.postSecure(
        `${CANDIDATE_BASE}/${candidateId}/job-fit/analyze`
      );
      return response.data;
    } catch (error) {
      console.error("Error triggering job-fit analysis:", error);
      throw error;
    }
  },

  fetchJobFitResults: async (candidateId) => {
    try {
      const response = await apiService.getSecure(
        `${CANDIDATE_BASE}/${candidateId}/job-fit`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching job-fit results:", error);
      throw error;
    }
  },

  getResumeReuploadStatus: async (candidateId, applicationId) => {
    try {
      const response = await apiService.getSecure(
        `${CANDIDATE_BASE}/resume-reupload/${candidateId}/status`,
        { params: { application_id: applicationId } }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching resume reupload status:", error);
      throw error;
    }
  },
  // ── Candidate status undo ───────────────────────────────────────────────
  getStatusUndoConfirmation: async () => {
    try {
      const response = await apiService.getSecure(
        `${CANDIDATE_BASE}/status/undo-confirmation`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching status undo confirmation:", error);
      throw error;
    }
  },

  undoCandidateStatus: async (candidateId, applicationId, confirmationText, undoReason) => {
    try {
      const payload = {
        candidate_id: candidateId,
        application_id: applicationId,
        confirmation_text: confirmationText,
        undo_reason: undoReason,
      };

      const response = await apiService.patch(
        `${CANDIDATE_BASE}/applications-status-undo`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error undoing candidate status:", error);
      throw error;
    }
  },

  // ── Apply from Suitable Jobs ────────────────────────────────────────────
  // Which jobs this candidate already has an application on. Same states the
  // job-fit rows carry (`apply_state`); jobs absent from `results` are free to
  // apply to.
  fetchCandidateApplyStates: async (candidateId) => {
    try {
      const response = await apiService.getSecure(
        `${CANDIDATE_BASE}/${candidateId}/apply-states`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching candidate apply states:", error);
      throw error;
    }
  },

  fetchJobFitApplyConsentText: async () => {
    try {
      const response = await apiService.getSecure(
        `${CANDIDATE_BASE}/jobfit-apply/consent-text`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching job-fit apply consent text:", error);
      throw error;
    }
  },

  fetchJobFitApplyPrefill: async (candidateId, jobId) => {
    try {
      const response = await apiService.getSecure(
        `${CANDIDATE_BASE}/${candidateId}/jobfit-apply/${jobId}/prefill`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching job-fit apply prefill:", error);
      throw error;
    }
  },

  submitJobFitApply: async (candidateId, jobId, payload) => {
    try {
      const response = await apiService.postSecure(
        `${CANDIDATE_BASE}/${candidateId}/jobfit-apply/${jobId}`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error submitting job-fit apply:", error);
      throw error;
    }
  },

  finalizeJobFitApplyConfirmation: async (token) => {
    try {
      const response = await apiService.post(
        `${CANDIDATE_BASE}/jobfit-apply/finalize?token=${token}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};
